/**
 * Import-Verlauf für den Mitarbeiter-Import.
 * Listet die letzten Import-Läufe und erlaubt das Rückgängigmachen (Undo)
 * einzelner Läufe: stellt alte Werte wieder her und löscht neu angelegte MA.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, History, RotateCcw, FileSpreadsheet } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import api from '../lib/api';
import { toast } from 'sonner';

export const EmployeeImportHistoryDialog = ({ open, onOpenChange, onChanged }) => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [undoingId, setUndoingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/employees/import/batches');
      setBatches(res.data?.batches || []);
    } catch { toast.error('Verlauf konnte nicht geladen werden'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const undo = async (batchId) => {
    setUndoingId(batchId);
    try {
      const res = await api.post(`/employees/import/batches/${batchId}/undo`);
      toast.success(`Rückgängig: ${res.data.restored_updates || 0} wiederhergestellt, ${res.data.deleted_created || 0} gelöscht`);
      await load();
      onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Rückgängig fehlgeschlagen'); }
    finally { setUndoingId(null); }
  };

  const fmt = (d) => { try { return format(parseISO(d), 'dd.MM.yyyy HH:mm', { locale: de }); } catch { return d; } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="import-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-primary" /> Import-Verlauf</DialogTitle>
          <DialogDescription>Vergangene Mitarbeiter-Importe. Ein Lauf kann rückgängig gemacht werden.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : batches.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <FileSpreadsheet className="h-10 w-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Noch keine Importe durchgeführt.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {batches.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 rounded-md border border-border p-3" data-testid={`import-history-row-${b.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate">{b.filename || 'Import'}</span>
                    {b.undone
                      ? <Badge variant="outline" className="text-[10px]">Rückgängig gemacht</Badge>
                      : <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100 text-[10px]">Aktiv</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {fmt(b.created_at)} · {b.created_by_name || '—'}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {(b.summary?.created || 0)} angelegt · {(b.summary?.updated || 0)} aktualisiert · {(b.summary?.skipped || 0)} übersprungen
                  </div>
                </div>
                {!b.undone && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-amber-700 border-amber-300 hover:bg-amber-50 flex-shrink-0"
                    onClick={() => undo(b.id)}
                    disabled={undoingId === b.id}
                    data-testid={`import-history-undo-${b.id}`}
                  >
                    {undoingId === b.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
                    Rückgängig
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end pt-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Schließen</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
